import React, { useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts";
import ReactECharts from "echarts-for-react";
import { COLOR_CODES } from "../../common";
import { lightenColor } from "../../components/recharts/ReMixCharts";

interface BuyerSellerChartProps {
  data: any[];
  showLegend: boolean;
  theme?: "light" | "dark";
  type?: "buyer" | "seller";
  height?: number;
  onChartEvents?: any; // Accept onChartEvents as a prop
}

export const BuyerSellerChart: React.FC<BuyerSellerChartProps> = ({
  data,
  showLegend,
  theme = "light",
  type = "buyer",
  height = 400,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  // Color schemes based on type
  const colorSchemes = {
    buyer: {
      gradient: [lightenColor(COLOR_CODES.primary, 40), COLOR_CODES.primary], // Using your primary color
      light: "#FFF4E6",
      main: lightenColor(COLOR_CODES.primary, 40),
      dark: COLOR_CODES.primary,
    },
    seller: {
      gradient: ["#E8F4F5", COLOR_CODES.secondary], // Light teal to your secondary color
      light: "#F0F8F9",
      main: COLOR_CODES.secondary,
      dark: COLOR_CODES.secondary, // Using your secondary color
    },
  };

  const currentColors = colorSchemes[type];

  // Define option outside useEffect so it's accessible in JSX
  const sortedData = [...data]
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value)
    .reverse();

  const chartData = sortedData.map((item, index) => ({
    name: item.name,
    value: item.value / 1000000, // Convert to Million MWh
    itemStyle: {
      color: {
        type: "linear" as const,
        x: 0,
        y: 0,
        x2: 1,
        y2: 0,
        colorStops: [
          {
            offset: 0,
            color: type === "seller" ? COLOR_CODES.secondary : COLOR_CODES.primary,
          },
          {
            offset: 0.7,
            color: type === "seller" ? COLOR_CODES.secondary : COLOR_CODES.primary,
          },
          {
            offset: 1,
            color: type === "seller" ? COLOR_CODES.secondary : COLOR_CODES.primary,
          },
        ],
      },
      borderRadius: [0, 8, 8, 0],
      shadowColor: "rgba(0, 0, 0, 0.1)",
      shadowBlur: 4,
      shadowOffsetX: 2,
      shadowOffsetY: 2,
    },
    emphasis: {
      itemStyle: {
        color: {
          type: "linear" as const,
          x: 0,
          y: 0,
          x2: 1,
          y2: 0,
          colorStops: [
            {
              offset: 0,
              color: currentColors.main,
            },
            {
              offset: 1,
              color: currentColors.dark,
            },
          ],
        },
        shadowColor: "rgba(0, 0, 0, 0.2)",
        shadowBlur: 8,
        shadowOffsetX: 4,
        shadowOffsetY: 4,
      },
    },
  }));

  const option: echarts.EChartsOption = useMemo(
    () => ({
      backgroundColor: theme === "dark" ? "#1F2937" : "transparent",
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
          shadowStyle: {
            color: "rgba(0, 0, 0, 0.1)",
          },
        },
        formatter: function (params: any) {
          if (!params || params.length === 0) return "";
          const param = params[0];
          const value = parseFloat(param.value.toString())
            .toFixed(2)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ",");

          return `
         <div style="
font-family: 'Inter', sans-serif;
padding: 12px 16px;
background: linear-gradient(135deg, ${currentColors.main}15, ${currentColors.dark}15);
border-left: 4px solid ${currentColors.main};
border-radius: 8px;
box-shadow: 0 4px 12px rgba(0,0,0,0.15);
max-width: 250px;
word-break: break-all;
overflow-wrap: break-word;
white-space: normal;
">
<div style="
  font-weight: 700; 
  font-size: 14px; 
  color: #1F2937; 
  margin-bottom: 8px; 
  word-break: break-all;
  overflow-wrap: break-word;
  white-space: normal;
  line-height: 1.3;
">
  ${param.name}
</div>
<div style="display: flex; align-items: center; gap: 4px;">
  <span style="color: #6B7280; font-size: 12px;">Volume (Million MWh):</span>
  <span style="font-weight: 600; color: ${currentColors.dark}; font-size: 14px;">
    ${value}
  </span>
</div>
</div>
        `;
        },
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        borderWidth: 0,
        borderRadius: 12,
        padding: 0,
        textStyle: {
          color: "#1F2937",
          fontFamily: "Inter, sans-serif",
        },
        extraCssText: `
        backdrop-filter: blur(10px);
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        border: 1px solid rgba(255, 255, 255, 0.2);
      `,
      },
      grid: {
        left: 20,
        right: 60,
        top: 5,
        bottom: 30,
        containLabel: true,
      },
      xAxis: {
        type: "value",
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: {
          show: true,
          lineStyle: {
            color: theme === "dark" ? "#374151" : "#F3F4F6",
            type: "dashed",
            width: 1,
          },
        },
      },
      yAxis: {
        type: "category",
        data: chartData.map((item) => item.name),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          fontSize: 11,
          color: theme === "dark" ? "#D1D5DB" : "#4B5563",
          fontFamily: "Inter, sans-serif",
          fontWeight: 500,
          width: 100,
          overflow: "break",
          interval: 0,
          margin: 10,
          lineHeight: 14,
          rich: {
            name: {
              fontSize: 10,
              fontWeight: 500,
              color: theme === "dark" ? "#D1D5DB" : "#4B5563",
              lineHeight: 14,
              width: 170,
            },
          },
          formatter: function (value: string) {
            return value;
          },
        },
        inverse: false,
      },
      series: [
        {
          type: "bar",
          data: chartData,
          barWidth: 35,
          barCategoryGap: "25%",
          label: {
            show: true,
            position: "insideLeft",
            fontSize: 11,
            color: "#FFFFFF",
            fontWeight: 600,
            fontFamily: "Inter, sans-serif",
            formatter: function (params: any) {
              const value = parseFloat(params.value);
              const formattedValue = value
                .toFixed(1)
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
              return `${formattedValue} Million MWh`;
            },
            distance: 8,
          },
          animationDelay: () => 100,
          animationDuration: 100,
          animationEasing: "cubicOut",
        },
      ],
      animation: true,
      animationDuration: 100,
      animationEasing: "cubicOut",
    }),
    [theme, currentColors.main, currentColors.dark, chartData]
  );

  useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return;

    // Initialize chart
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    // Set option
    chartInstance.current.setOption(option, true);

    // Handle resize
    const handleResize = () => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [
    currentColors.dark,
    currentColors.main,
    data,
    showLegend,
    theme,
    type,
    option,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, []);

  const onChartEvents = {
    finished: () => {
    },
  };

  // Update the return statement to remove fixed height
  return (
    <div className="w-full relative">
      <ReactECharts
        option={option}
        onEvents={onChartEvents} // <-- Add this line
        style={{
          height: `${Math.max(
            data.filter((item) => item.value > 0).length * 60,
            400
          )}px`,
          width: "100%",
        }}
        theme={theme === "dark" ? "dark" : undefined}
      />
    </div>
  );
};

export default BuyerSellerChart;
